import { useCallback, useState } from "react"
import { useFormik } from "formik"
import { toast } from "react-toastify"
import {
  Button,
  Card,
  CardBody,
  Col,
  Form,
  FormFeedback,
  Input,
  Label,
  Row,
} from "reactstrap"
import * as Yup from "yup"

import { CanView } from "components/Common/CanView"
import InputField from "components/Common/InputField"
import PageWrapper from "components/Common/PageWrapper"
import { ACCESS } from "constants/accessPermissions"
import {
  useAttendanceSettings,
  useUpdateAttendanceSettings,
} from "hooks/attendanceHook"

const WEEK_DAYS = [
  "saturday",
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
]

const pad2 = n => String(n).padStart(2, "0")

const toTimeInput = time => {
  if (!time) return ""
  const match = String(time).match(/^(\d{1,2}):(\d{2})/)
  if (!match) return ""
  return `${pad2(match[1])}:${match[2]}`
}

const AttendanceSettings = () => {
  document.title = "Attendance Settings"

  const { data: response, isLoading } = useAttendanceSettings()
  const settings = response?.data

  const { mutateAsync: updateSettings, isPending: saving } =
    useUpdateAttendanceSettings()

  const [locating, setLocating] = useState(false)

  const validationSchema = Yup.object({
    office_start_time: Yup.string().required("Please set office start time"),
    office_end_time: Yup.string()
      .required("Please set office end time")
      .test(
        "after-start",
        "End time must be after start time",
        function (value) {
          if (!value || !this.parent.office_start_time) return true
          return value > this.parent.office_start_time
        }
      ),
    grace_minutes: Yup.number()
      .required("Please set grace minutes")
      .min(0)
      .max(480),
    geofence_enabled: Yup.boolean(),
    geofence_lat: Yup.number()
      .nullable()
      .when("geofence_enabled", {
        is: true,
        then: schema =>
          schema
            .required("Latitude is required when geofence is enabled")
            .min(-90)
            .max(90),
      }),
    geofence_lng: Yup.number()
      .nullable()
      .when("geofence_enabled", {
        is: true,
        then: schema =>
          schema
            .required("Longitude is required when geofence is enabled")
            .min(-180)
            .max(180),
      }),
    geofence_radius_m: Yup.number().required().min(10).max(100000),
    attendance_timezone: Yup.string().required("Please set a timezone"),
    attendance_notifications_enabled: Yup.boolean(),
    before_checkin_reminder_enabled: Yup.boolean(),
    before_checkin_offset_minutes: Yup.number().required().min(0).max(480),
    late_checkin_reminder_enabled: Yup.boolean(),
    absence_nudge_enabled: Yup.boolean(),
    absence_repeat_interval_minutes: Yup.number().required().min(15).max(720),
    late_checkout_reminder_enabled: Yup.boolean(),
    late_checkout_offset_minutes: Yup.number().required().min(0).max(480),
  })

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      office_start_time: toTimeInput(settings?.office_start_time),
      office_end_time: toTimeInput(settings?.office_end_time),
      grace_minutes: settings?.grace_minutes ?? 15,
      geofence_enabled: settings?.geofence_enabled ?? false,
      geofence_lat: settings?.geofence_lat ?? "",
      geofence_lng: settings?.geofence_lng ?? "",
      geofence_radius_m: settings?.geofence_radius_m ?? 200,
      weekend_days: settings?.weekend_days ?? ["friday"],
      attendance_timezone: settings?.attendance_timezone ?? "Asia/Dhaka",
      attendance_notifications_enabled:
        settings?.attendance_notifications_enabled ?? true,
      before_checkin_reminder_enabled:
        settings?.before_checkin_reminder_enabled ?? true,
      before_checkin_offset_minutes:
        settings?.before_checkin_offset_minutes ?? 15,
      late_checkin_reminder_enabled:
        settings?.late_checkin_reminder_enabled ?? true,
      absence_nudge_enabled: settings?.absence_nudge_enabled ?? true,
      absence_repeat_interval_minutes:
        settings?.absence_repeat_interval_minutes ?? 120,
      late_checkout_reminder_enabled:
        settings?.late_checkout_reminder_enabled ?? true,
      late_checkout_offset_minutes:
        settings?.late_checkout_offset_minutes ?? 60,
    },
    validationSchema,
    onSubmit: async values => {
      try {
        const payload = {
          ...values,
          geofence_lat:
            values.geofence_enabled && values.geofence_lat !== ""
              ? Number(values.geofence_lat)
              : null,
          geofence_lng:
            values.geofence_enabled && values.geofence_lng !== ""
              ? Number(values.geofence_lng)
              : null,
          grace_minutes: Number(values.grace_minutes),
          geofence_radius_m: Number(values.geofence_radius_m),
          before_checkin_offset_minutes: Number(
            values.before_checkin_offset_minutes
          ),
          absence_repeat_interval_minutes: Number(
            values.absence_repeat_interval_minutes
          ),
          late_checkout_offset_minutes: Number(
            values.late_checkout_offset_minutes
          ),
        }
        const res = await updateSettings(payload)
        toast.success(res?.message ?? "Settings updated successfully.")
      } catch (error) {
        console.error(error)
        toast.error(
          error?.response?.data?.message ?? "Failed to update settings."
        )
      }
    },
  })

  const useCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by this browser.")
      return
    }
    setLocating(true)
    navigator.geolocation.getCurrentPosition(
      position => {
        formik.setFieldValue(
          "geofence_lat",
          Number(position.coords.latitude.toFixed(7))
        )
        formik.setFieldValue(
          "geofence_lng",
          Number(position.coords.longitude.toFixed(7))
        )
        setLocating(false)
        toast.success("Current location captured.")
      },
      () => {
        setLocating(false)
        toast.error("Could not get your location. Please allow location access.")
      },
      { enableHighAccuracy: true, timeout: 15000 }
    )
  }, [formik])

  const toggleWeekendDay = useCallback(
    day => {
      const current = formik.values.weekend_days || []
      const next = current.includes(day)
        ? current.filter(d => d !== day)
        : [...current, day]
      formik.setFieldValue("weekend_days", next)
    },
    [formik]
  )

  return (
    <PageWrapper title="Attendance Settings" breadcrumbItem="Attendance Settings">
      <Row>
        <Col lg={8}>
          <Card>
            <CardBody>
              {isLoading ? (
                <p className="text-muted mb-0">Loading settings...</p>
              ) : (
                <Form
                  onSubmit={e => {
                    e.preventDefault()
                    formik.handleSubmit()
                  }}
                >
                  <h5 className="mb-3">Office Hours</h5>
                  <Row>
                    <Col sm={4} className="mb-3">
                      <Label htmlFor="office_start_time">
                        Start Time <span className="text-danger">*</span>
                      </Label>
                      <InputField
                        type="time"
                        name="office_start_time"
                        id="office_start_time"
                        value={formik.values.office_start_time}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={
                          formik.touched.office_start_time &&
                          !!formik.errors.office_start_time
                        }
                      />
                      {formik.touched.office_start_time &&
                        formik.errors.office_start_time && (
                          <FormFeedback type="invalid">
                            {formik.errors.office_start_time}
                          </FormFeedback>
                        )}
                    </Col>
                    <Col sm={4} className="mb-3">
                      <Label htmlFor="office_end_time">
                        End Time <span className="text-danger">*</span>
                      </Label>
                      <InputField
                        type="time"
                        name="office_end_time"
                        id="office_end_time"
                        value={formik.values.office_end_time}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={
                          formik.touched.office_end_time &&
                          !!formik.errors.office_end_time
                        }
                      />
                      {formik.touched.office_end_time &&
                        formik.errors.office_end_time && (
                          <FormFeedback type="invalid">
                            {formik.errors.office_end_time}
                          </FormFeedback>
                        )}
                    </Col>
                    <Col sm={4} className="mb-3">
                      <Label htmlFor="grace_minutes">
                        Grace Minutes <span className="text-danger">*</span>
                      </Label>
                      <InputField
                        type="number"
                        name="grace_minutes"
                        id="grace_minutes"
                        value={formik.values.grace_minutes}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={
                          formik.touched.grace_minutes &&
                          !!formik.errors.grace_minutes
                        }
                      />
                      {formik.touched.grace_minutes &&
                        formik.errors.grace_minutes && (
                          <FormFeedback type="invalid">
                            {formik.errors.grace_minutes}
                          </FormFeedback>
                        )}
                    </Col>
                  </Row>

                  <h5 className="mb-3 mt-2">Weekend Days</h5>
                  <div className="d-flex flex-wrap gap-3 mb-4">
                    {WEEK_DAYS.map(day => (
                      <div className="form-check" key={day}>
                        <Input
                          type="checkbox"
                          className="form-check-input"
                          id={`weekend-${day}`}
                          checked={(formik.values.weekend_days || []).includes(
                            day
                          )}
                          onChange={() => toggleWeekendDay(day)}
                        />
                        <Label
                          className="form-check-label text-capitalize"
                          htmlFor={`weekend-${day}`}
                        >
                          {day}
                        </Label>
                      </div>
                    ))}
                  </div>

                  <h5 className="mb-1 mt-2">Notifications</h5>
                  <p className="text-muted small mb-3">
                    Control when backend attendance reminders are sent to the
                    mobile app.
                  </p>

                  <div className="form-check form-switch mb-3">
                    <Input
                      type="switch"
                      className="form-check-input"
                      id="attendance_notifications_enabled"
                      name="attendance_notifications_enabled"
                      checked={formik.values.attendance_notifications_enabled}
                      onChange={formik.handleChange}
                    />
                    <Label
                      className="form-check-label"
                      htmlFor="attendance_notifications_enabled"
                    >
                      Enable attendance notifications
                    </Label>
                  </div>

                  {formik.values.attendance_notifications_enabled && (
                    <>
                      <Row>
                        <Col sm={6} className="mb-3">
                          <div className="form-check form-switch mb-2">
                            <Input
                              type="switch"
                              className="form-check-input"
                              id="before_checkin_reminder_enabled"
                              name="before_checkin_reminder_enabled"
                              checked={
                                formik.values.before_checkin_reminder_enabled
                              }
                              onChange={formik.handleChange}
                            />
                            <Label
                              className="form-check-label"
                              htmlFor="before_checkin_reminder_enabled"
                            >
                              Before check-in reminder
                            </Label>
                          </div>
                          <InputField
                            type="number"
                            name="before_checkin_offset_minutes"
                            id="before_checkin_offset_minutes"
                            value={formik.values.before_checkin_offset_minutes}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            disabled={
                              !formik.values.before_checkin_reminder_enabled
                            }
                            invalid={
                              formik.touched.before_checkin_offset_minutes &&
                              !!formik.errors.before_checkin_offset_minutes
                            }
                          />
                          <div className="small text-muted mt-1">
                            Minutes before office start time.
                          </div>
                          {formik.touched.before_checkin_offset_minutes &&
                            formik.errors.before_checkin_offset_minutes && (
                              <FormFeedback type="invalid">
                                {formik.errors.before_checkin_offset_minutes}
                              </FormFeedback>
                            )}
                        </Col>

                        <Col sm={6} className="mb-3">
                          <div className="form-check form-switch mb-2">
                            <Input
                              type="switch"
                              className="form-check-input"
                              id="late_checkin_reminder_enabled"
                              name="late_checkin_reminder_enabled"
                              checked={
                                formik.values.late_checkin_reminder_enabled
                              }
                              onChange={formik.handleChange}
                            />
                            <Label
                              className="form-check-label"
                              htmlFor="late_checkin_reminder_enabled"
                            >
                              Late check-in reminder
                            </Label>
                          </div>
                          <div className="border rounded p-2 small text-muted">
                            Sent after office start plus grace minutes.
                          </div>
                        </Col>
                      </Row>

                      <Row>
                        <Col sm={6} className="mb-3">
                          <div className="form-check form-switch mb-2">
                            <Input
                              type="switch"
                              className="form-check-input"
                              id="absence_nudge_enabled"
                              name="absence_nudge_enabled"
                              checked={formik.values.absence_nudge_enabled}
                              onChange={formik.handleChange}
                            />
                            <Label
                              className="form-check-label"
                              htmlFor="absence_nudge_enabled"
                            >
                              Absence repeat reminder
                            </Label>
                          </div>
                          <InputField
                            type="number"
                            name="absence_repeat_interval_minutes"
                            id="absence_repeat_interval_minutes"
                            value={
                              formik.values.absence_repeat_interval_minutes
                            }
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            disabled={!formik.values.absence_nudge_enabled}
                            invalid={
                              formik.touched.absence_repeat_interval_minutes &&
                              !!formik.errors.absence_repeat_interval_minutes
                            }
                          />
                          <div className="small text-muted mt-1">
                            Repeat interval in minutes until office end.
                          </div>
                          {formik.touched.absence_repeat_interval_minutes &&
                            formik.errors.absence_repeat_interval_minutes && (
                              <FormFeedback type="invalid">
                                {formik.errors.absence_repeat_interval_minutes}
                              </FormFeedback>
                            )}
                        </Col>

                        <Col sm={6} className="mb-3">
                          <div className="form-check form-switch mb-2">
                            <Input
                              type="switch"
                              className="form-check-input"
                              id="late_checkout_reminder_enabled"
                              name="late_checkout_reminder_enabled"
                              checked={
                                formik.values.late_checkout_reminder_enabled
                              }
                              onChange={formik.handleChange}
                            />
                            <Label
                              className="form-check-label"
                              htmlFor="late_checkout_reminder_enabled"
                            >
                              Late check-out reminder
                            </Label>
                          </div>
                          <InputField
                            type="number"
                            name="late_checkout_offset_minutes"
                            id="late_checkout_offset_minutes"
                            value={formik.values.late_checkout_offset_minutes}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            disabled={
                              !formik.values.late_checkout_reminder_enabled
                            }
                            invalid={
                              formik.touched.late_checkout_offset_minutes &&
                              !!formik.errors.late_checkout_offset_minutes
                            }
                          />
                          <div className="small text-muted mt-1">
                            Minutes after office end time.
                          </div>
                          {formik.touched.late_checkout_offset_minutes &&
                            formik.errors.late_checkout_offset_minutes && (
                              <FormFeedback type="invalid">
                                {formik.errors.late_checkout_offset_minutes}
                              </FormFeedback>
                            )}
                        </Col>
                      </Row>
                    </>
                  )}

                  <h5 className="mb-1 mt-2">Geofence</h5>
                  <p className="text-muted small mb-3">
                    When enabled, employees can only check in/out within the
                    radius of the configured location.
                  </p>

                  <div className="form-check form-switch mb-3">
                    <Input
                      type="switch"
                      className="form-check-input"
                      id="geofence_enabled"
                      name="geofence_enabled"
                      checked={formik.values.geofence_enabled}
                      onChange={formik.handleChange}
                    />
                    <Label
                      className="form-check-label"
                      htmlFor="geofence_enabled"
                    >
                      Enable geofence validation
                    </Label>
                  </div>

                  {formik.values.geofence_enabled && (
                    <Row>
                      <Col sm={4} className="mb-3">
                        <Label htmlFor="geofence_lat">
                          Latitude <span className="text-danger">*</span>
                        </Label>
                        <InputField
                          type="number"
                          step="any"
                          name="geofence_lat"
                          id="geofence_lat"
                          value={formik.values.geofence_lat}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          invalid={
                            formik.touched.geofence_lat &&
                            !!formik.errors.geofence_lat
                          }
                        />
                        {formik.touched.geofence_lat &&
                          formik.errors.geofence_lat && (
                            <FormFeedback type="invalid">
                              {formik.errors.geofence_lat}
                            </FormFeedback>
                          )}
                      </Col>
                      <Col sm={4} className="mb-3">
                        <Label htmlFor="geofence_lng">
                          Longitude <span className="text-danger">*</span>
                        </Label>
                        <InputField
                          type="number"
                          step="any"
                          name="geofence_lng"
                          id="geofence_lng"
                          value={formik.values.geofence_lng}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          invalid={
                            formik.touched.geofence_lng &&
                            !!formik.errors.geofence_lng
                          }
                        />
                        {formik.touched.geofence_lng &&
                          formik.errors.geofence_lng && (
                            <FormFeedback type="invalid">
                              {formik.errors.geofence_lng}
                            </FormFeedback>
                          )}
                      </Col>
                      <Col sm={4} className="mb-3">
                        <Label htmlFor="geofence_radius_m">
                          Radius (meters) <span className="text-danger">*</span>
                        </Label>
                        <InputField
                          type="number"
                          name="geofence_radius_m"
                          id="geofence_radius_m"
                          value={formik.values.geofence_radius_m}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          invalid={
                            formik.touched.geofence_radius_m &&
                            !!formik.errors.geofence_radius_m
                          }
                        />
                        {formik.touched.geofence_radius_m &&
                          formik.errors.geofence_radius_m && (
                            <FormFeedback type="invalid">
                              {formik.errors.geofence_radius_m}
                            </FormFeedback>
                          )}
                      </Col>
                      <Col sm={12} className="mb-3">
                        <Button
                          type="button"
                          color="light"
                          onClick={useCurrentLocation}
                          disabled={locating}
                        >
                          <i className="bx bx-current-location me-1" />
                          {locating ? "Locating..." : "Use my current location"}
                        </Button>
                      </Col>
                    </Row>
                  )}

                  <h5 className="mb-3 mt-2">Timezone</h5>
                  <Row>
                    <Col sm={6} className="mb-3">
                      <Label htmlFor="attendance_timezone">
                        Attendance Timezone{" "}
                        <span className="text-danger">*</span>
                      </Label>
                      <InputField
                        type="text"
                        name="attendance_timezone"
                        id="attendance_timezone"
                        placeholder="e.g. Asia/Dhaka"
                        value={formik.values.attendance_timezone}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={
                          formik.touched.attendance_timezone &&
                          !!formik.errors.attendance_timezone
                        }
                      />
                      {formik.touched.attendance_timezone &&
                        formik.errors.attendance_timezone && (
                          <FormFeedback type="invalid">
                            {formik.errors.attendance_timezone}
                          </FormFeedback>
                        )}
                    </Col>
                  </Row>

                  <CanView access={[ACCESS.ATTENDANCE.EDIT_SETTINGS]}>
                    <div className="text-end">
                      <Button color="success" type="submit" disabled={saving}>
                        {saving ? "Saving..." : "Save Settings"}
                      </Button>
                    </div>
                  </CanView>
                </Form>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </PageWrapper>
  )
}

export default AttendanceSettings
